import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Project, ProjectEvent } from '@/types';

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('projects')
        .select('*, clients(*)')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;
      setProjects((data as Project[]) || []);
    } catch {
      setProjects([]);
      setError('No se pudieron cargar los proyectos. Verifique su conexión e intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { projects, loading, error, reload: load };
}

export function useProject(id?: string) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setProject(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('projects')
        .select('*, clients(*)')
        .eq('id', id)
        .maybeSingle();

      if (queryError) throw queryError;
      setProject((data as Project) || null);
    } catch {
      setProject(null);
      setError('No se pudo cargar el proyecto.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { project, loading, error, reload: load };
}

export function useProjectEvents(projectId?: string) {
  const [events, setEvents] = useState<ProjectEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from('project_events')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
      if (queryError) throw queryError;
      setEvents((data as ProjectEvent[]) || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  return { events, loading, reload: load };
}